import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import TeamRankingWidget from '../../components/stats/TeamRankingWidget';
import { getRankingCompetitionMeta, getWidgetConfiguration } from '../../../api';
import { resolveWidgetDisplayTitle } from '../../utils/widgetDisplayTitle';

const MAX_SELECTED_COMPETITIONS = 5;

const clampCompetitionIds = (value = '') =>
  String(value || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
    .slice(0, MAX_SELECTED_COMPETITIONS);

export default function TeamRankingPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const competitionIdsParam = searchParams.get('competitionIds') || '';
  const widgetConfigurationId = searchParams.get('widgetConfigurationId') || '';

  const statType = searchParams.get('statType') || '';

  const handleStatTypeChange = (nextStatType, options = {}) => {
  const nextParams = new URLSearchParams(searchParams);

  if (options.isDefaultTab) {
    nextParams.delete('statType');
  } else {
    nextParams.set('statType', nextStatType);
  }

  if (widgetConfigurationId) {
    nextParams.set('widgetConfigurationId', widgetConfigurationId);
  }

  setSearchParams(nextParams, { replace: true });
};

  const [widgetConfig, setWidgetConfig] = useState(null);

const competitionIds = useMemo(
  () => clampCompetitionIds(competitionIdsParam),
  [competitionIdsParam]
);

  const firstCompetitionId = competitionIds[0];

  const [discipline, setDiscipline] = useState(null);
  const [season, setSeason] = useState(null);
  const [title, setTitle] = useState('Statistiques équipes');
  const [metaResolved, setMetaResolved] = useState(false);

  useEffect(() => {
    let ignore = false;

    const loadMeta = async () => {
      setMetaResolved(false);

      if (!firstCompetitionId) {
        if (!ignore) {
          setDiscipline(null);
          setSeason(null);
          setTitle('Statistiques équipes');
          setMetaResolved(true);
        }
        return;
      }

      try {
        const { data } = await getRankingCompetitionMeta(firstCompetitionId);

        if (!ignore) {
          setDiscipline(data?.discipline || null);
          setSeason(data?.season || null);
          setTitle(
            competitionIds.length > 1
              ? 'Statistiques équipes'
              : data?.name || 'Statistiques équipes'
          );
          setMetaResolved(true);
        }
      } catch (error) {
        if (!ignore) {
          setDiscipline(null);
          setSeason(null);
          setTitle('Statistiques équipes');
          setMetaResolved(true);
        }
      }
    };

    loadMeta();

    return () => {
      ignore = true;
    };
  }, [firstCompetitionId, competitionIds.length]);

  useEffect(() => {
  let ignore = false;

  const loadWidgetConfig = async () => {
    if (!widgetConfigurationId) {
      if (!ignore) setWidgetConfig(null);
      return;
    }

    try {
      const { data } = await getWidgetConfiguration(widgetConfigurationId);
      if (!ignore) setWidgetConfig(data || null);
    } catch {
      if (!ignore) setWidgetConfig(null);
    }
  };

  loadWidgetConfig();

  return () => {
    ignore = true;
  };
}, [widgetConfigurationId]);

const competitionInfos = useMemo(
  () =>
    competitionIds.length === 1 && title && title !== 'Statistiques équipes'
      ? [{ name: title }]
      : [],
  [competitionIds.length, title]
);

const resolvedTitle = useMemo(
  () =>
    resolveWidgetDisplayTitle({
      widgetConfig,
      competitionInfos,
      fallbackTitle: title,
    }),
  [widgetConfig, competitionInfos, title]
);

const shouldShowWidgetHeader = Boolean(resolvedTitle);

  if (!metaResolved) return null;
  if (!discipline || competitionIds.length === 0) return null;

  return (
    <TeamRankingWidget
      discipline={discipline}
      season={season}
      competitionIds={competitionIds}
      widgetConfigurationId={widgetConfigurationId || null}
      title={resolvedTitle}
      showCompetitionInfos={shouldShowWidgetHeader}
      statType={statType}
      onStatTypeChange={handleStatTypeChange}
      onNavigate={(url) => navigate(url)}
    />
  );
}